/**
 * Real API services for social media downloaders
 */

// YouTube API integration
export const youtubeService = {
  // Extract video ID from YouTube URL
  extractVideoId: (url: string): string | null => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /^([a-zA-Z0-9_-]{11})$/
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  },

  // Get video information using YouTube oEmbed API
  getVideoInfo: async (videoId: string) => {
    try {
      const oEmbedUrl = `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`;
      const response = await fetch(oEmbedUrl);
      
      if (!response.ok) {
        throw new Error('Video not found or private');
      }
      
      const data = await response.json();
      return {
        title: data.title,
        author: data.author_name,
        thumbnailUrl: data.thumbnail_url,
        width: data.thumbnail_width,
        height: data.thumbnail_height
      };
    } catch (error) {
      throw new Error('Failed to fetch video information');
    }
  },

  // Get all thumbnail qualities
  getThumbnails: (videoId: string) => {
    return [
      {
        quality: 'Max Resolution (1280x720)',
        url: `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`,
        width: 1280,
        height: 720
      },
      {
        quality: 'Standard Definition (640x480)',
        url: `https://img.youtube.com/vi/${videoId}/sddefault.jpg`,
        width: 640,
        height: 480
      },
      {
        quality: 'High Quality (480x360)',
        url: `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
        width: 480,
        height: 360
      },
      {
        quality: 'Medium Quality (320x180)',
        url: `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`,
        width: 320,
        height: 180
      },
      {
        quality: 'Default (120x90)',
        url: `https://img.youtube.com/vi/${videoId}/default.jpg`,
        width: 120,
        height: 90
      }
    ];
  }
};

// Instagram API integration (using public APIs)
export const instagramService = {
  // Extract username from Instagram URL
  extractUsername: (input: string): string => {
    // Remove @ symbol and clean up
    let username = input.replace('@', '').trim();
    
    // Extract from URL if provided
    const urlMatch = username.match(/(?:instagram\.com\/)([^\/\?]+)/);
    if (urlMatch) {
      username = urlMatch[1];
    }
    
    return username;
  },

  // Get profile information using Instagram Basic Display API or public endpoints
  getProfileInfo: async (_username: string) => {
    try {
      // Note: Instagram has strict API policies. For production, you'd need:
      // 1. Instagram Basic Display API with proper authentication
      // 2. Instagram Graph API for business accounts
      // 3. Third-party APIs that comply with Instagram's ToS
      
      // For now, we'll simulate the data structure but indicate that real implementation requires proper API access
      throw new Error('Instagram API requires authentication. Please use Instagram\'s official APIs with proper credentials.');
      
      // Real implementation would look like:
      // const response = await fetch(`https://graph.instagram.com/${username}?fields=id,username,media_count,followers_count&access_token=${accessToken}`);
      // return await response.json();
    } catch (error) {
      throw new Error('Instagram profile data requires authenticated API access');
    }
  }
};

// Twitter API integration
export const twitterService = {
  // Extract tweet info from Twitter/X URL
  extractTweetInfo: (url: string) => {
    const patterns = [
      /(?:twitter\.com|x\.com)\/([^\/]+)\/status\/(\d+)/,
      /(?:twitter\.com|x\.com)\/i\/web\/status\/(\d+)/
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) {
        return {
          username: match[1] || 'unknown',
          tweetId: match[2] || match[1]
        };
      }
    }
    return null;
  },

  // Get tweet data using Twitter's oEmbed API (no auth required for public tweets)
  getTweetData: async (tweetId: string) => {
    try {
      const oEmbedUrl = `https://publish.twitter.com/oembed?url=https://twitter.com/x/status/${tweetId}&omit_script=true`;
      const response = await fetch(oEmbedUrl);
      
      if (!response.ok) {
        throw new Error('Tweet not found or private');
      }
      
      const data = await response.json();
      return {
        html: data.html,
        author_name: data.author_name,
        author_url: data.author_url,
        provider_name: data.provider_name,
        cache_age: data.cache_age,
        url: data.url
      };
    } catch (error) {
      throw new Error('Failed to fetch tweet data. Tweet may be private or deleted.');
    }
  },

  // Parse thread from tweet HTML (basic implementation)
  parseThreadFromHtml: (html: string, tweetId: string) => {
    // This is a simplified parser - in production you'd use a proper HTML parser
    const textMatch = html.match(/<p[^>]*>(.*?)<\/p>/s);
    const authorMatch = html.match(/href="https:\/\/twitter\.com\/([^"]+)"/);
    
    return {
      id: tweetId,
      text: textMatch ? textMatch[1].replace(/<[^>]*>/g, '').trim() : 'Tweet content not available',
      author: authorMatch ? authorMatch[1] : 'Unknown',
      timestamp: new Date().toISOString(),
      likes: 0, // Not available in oEmbed
      retweets: 0, // Not available in oEmbed
      replies: 0, // Not available in oEmbed
      order: 1
    };
  },

  // Generate thread structure (demo implementation)
  generateThreadStructure: (mainTweet: any, tweetId: string) => {
    return {
      threadId: tweetId,
      author: mainTweet.author || 'Demo User',
      authorHandle: `@${mainTweet.author || 'demo'}`,
      totalTweets: 1, // Would be calculated from actual thread
      tweets: [mainTweet],
      threadTitle: mainTweet.text ? mainTweet.text.substring(0, 50) + '...' : 'Twitter Thread',
      createdAt: new Date().toISOString()
    };
  }
};

// TikTok API integration
export const tiktokService = {
  // Extract video info from TikTok URL
  extractVideoInfo: (url: string) => {
    const patterns = [
      /tiktok\.com\/@([^\/]+)\/video\/(\d+)/,
      /vm\.tiktok\.com\/([^\/]+)/,
      /vt\.tiktok\.com\/([^\/]+)/
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) {
        return {
          username: match[1] || 'unknown',
          videoId: match[2] || match[1]
        };
      }
    }
    return null;
  },

  // Get video data (requires TikTok API access)
  getVideoData: async (_videoId: string) => {
    try {
      // Note: TikTok API requires business approval and authentication
      // Real implementation would use TikTok Research API or Creator API
      throw new Error('TikTok API requires business authentication and approval');
    } catch (error) {
      throw new Error('TikTok video data requires authenticated API access');
    }
  }
};

// Facebook API integration
export const facebookService = {
  // Extract video info from Facebook URL
  extractVideoInfo: (url: string) => {
    const patterns = [
      /facebook\.com\/watch\/?\?v=(\d+)/,
      /facebook\.com\/.*\/videos\/(\d+)/,
      /fb\.watch\/([^\/\?]+)/
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) {
        return {
          videoId: match[1]
        };
      }
    }
    return null;
  },

  // Get video data (requires Facebook API access)
  getVideoData: async (_videoId: string) => {
    try {
      // Note: Facebook API requires app approval and access tokens
      throw new Error('Facebook API requires authenticated access');
    } catch (error) {
      throw new Error('Facebook video data requires authenticated API access');
    }
  }
};

// Generic download utility for cross-origin resources
export const downloadService = {
  // Download file with CORS handling
  downloadFile: async (url: string, filename: string, options: { useProxy?: boolean } = {}) => {
    try {
      // Method 1: Try direct download first
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      
      // Check if we can access the URL directly
      try {
        await fetch(url, { method: 'HEAD', mode: 'no-cors' });
        // If no error, proceed with direct download
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return true;
      } catch {
        // Direct access failed, try proxy if requested
        if (options.useProxy) {
          return await downloadService.downloadWithProxy(url, filename);
        } else {
          // Fallback: open in new tab
          window.open(url, '_blank', 'noopener,noreferrer');
          return true;
        }
      }
    } catch (error) {
      console.error('Download failed:', error);
      return false;
    }
  },

  // Download using proxy service
  downloadWithProxy: async (url: string, filename: string) => {
    try {
      // Use CORS proxy services (note: these should be replaced with your own proxy in production)
      const proxyUrls = [
        `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`,
        `https://cors-anywhere.herokuapp.com/${url}`,
        `https://thingproxy.freeboard.io/fetch/${url}`
      ];

      for (const proxyUrl of proxyUrls) {
        try {
          const response = await fetch(proxyUrl);
          if (response.ok) {
            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);
            
            const link = document.createElement('a');
            link.href = objectUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            // Clean up
            URL.revokeObjectURL(objectUrl);
            return true;
          }
        } catch (proxyError) {
          console.log(`Proxy ${proxyUrl} failed, trying next...`);
        }
      }
      
      // If all proxies fail, open in new tab
      window.open(url, '_blank', 'noopener,noreferrer');
      return true;
    } catch (error) {
      console.error('Proxy download failed:', error);
      return false;
    }
  },

  // Validate image URL
  validateImageUrl: async (url: string): Promise<boolean> => {
    try {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(true);
        img.onerror = () => resolve(false);
        img.src = url;
        
        // Timeout after 5 seconds
        setTimeout(() => resolve(false), 5000);
      });
    } catch {
      return false;
    }
  },

  // Get file size if possible
  getFileSize: async (url: string): Promise<number | null> => {
    try {
      const response = await fetch(url, { method: 'HEAD', mode: 'cors' });
      const contentLength = response.headers.get('content-length');
      return contentLength ? parseInt(contentLength, 10) : null;
    } catch {
      return null;
    }
  },

  // Format file size
  formatFileSize: (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
};

// Error messages for API limitations
export const API_LIMITATIONS = {
  INSTAGRAM: 'Instagram API requires authentication and approval. For production use, implement Instagram Basic Display API or Graph API with proper credentials.',
  TIKTOK: 'TikTok API requires business verification and approval. Use TikTok Research API or Creator API for production.',
  FACEBOOK: 'Facebook API requires app approval and access tokens. Implement Facebook Graph API for production use.',
  TWITTER: 'Twitter API requires authentication. Use Twitter API v2 with proper credentials for production.',
  GENERAL: 'This tool demonstrates functionality. Production use requires proper API authentication and compliance with platform terms of service.'
};
